"use client";

import Image from "next/image";
import Link from "next/link";
import { Quote } from "lucide-react";
import {
  SITEGLUE_FOUNDER_DEFAULT_INSIGHT,
  SITEGLUE_FOUNDER_PHOTO,
  SITEGLUE_FOUNDER_PROFILE,
} from "~/lib/siteglue/blog-founder";
import { cn } from "~/lib/utils";

export type FounderInsightProps = {
  /** Short perspective — falls back to the SiteGlue default HITL insight. */
  insight?: string | null;
  className?: string;
  /** Compact for tight layouts (sidebar / mobile). */
  compact?: boolean;
};

/**
 * Reusable founder voice block for SiteGlue articles.
 * Reinforces authenticity vs AI-generated content and Always a Human-in-the-Loop™.
 */
export function FounderInsight({
  insight,
  className,
  compact = false,
}: FounderInsightProps) {
  const quote = (insight?.trim() || SITEGLUE_FOUNDER_DEFAULT_INSIGHT).trim();

  return (
    <aside
      className={cn(
        "not-prose rounded-2xl border border-[#4f81bd]/20 bg-gradient-to-br from-[#4f81bd]/[0.07] via-white to-[#9cbb5a]/[0.06] shadow-sm",
        compact ? "p-4" : "p-5 sm:p-6",
        className,
      )}
      data-testid="founder-insight"
      aria-label="Founder insight"
    >
      <div className={cn("flex gap-4", compact ? "items-start" : "items-start sm:gap-5")}>
        <div
          className={cn(
            "relative shrink-0 overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50 shadow-sm",
            compact ? "h-14 w-14" : "h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem]",
          )}
        >
          <Image
            src={SITEGLUE_FOUNDER_PHOTO}
            alt={`${SITEGLUE_FOUNDER_PROFILE.name}, ${SITEGLUE_FOUNDER_PROFILE.role}`}
            fill
            className="object-cover object-[center_18%]"
            sizes={compact ? "56px" : "72px"}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
              <Quote className="h-3 w-3" aria-hidden />
              Founder Insight
            </span>
            <span className="hidden text-slate-300 sm:inline" aria-hidden>
              ·
            </span>
            <span className="text-xs text-slate-500">Human voice, not AI filler</span>
          </div>

          <blockquote
            className={cn(
              "font-medium leading-relaxed text-slate-800",
              compact ? "text-sm" : "text-[15px] sm:text-base",
            )}
          >
            “{quote}”
          </blockquote>

          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <p className="font-semibold text-slate-900">{SITEGLUE_FOUNDER_PROFILE.name}</p>
            <span className="text-slate-300" aria-hidden>
              ·
            </span>
            <p className="text-slate-500">{SITEGLUE_FOUNDER_PROFILE.role}</p>
            {SITEGLUE_FOUNDER_PROFILE.aboutHref ? (
              <>
                <span className="text-slate-300" aria-hidden>
                  ·
                </span>
                <Link
                  href={SITEGLUE_FOUNDER_PROFILE.aboutHref}
                  className="font-medium text-[#4f81bd] hover:underline"
                >
                  About Jim
                </Link>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </aside>
  );
}
